<template>
    <div v-if="title || processedContent" class="py-5 lg:py-4 lg:pb-10">
        <h2 class="text-indian-khaki-950 text-4xl font-medium" v-if="title">{{ title }}</h2>
        <div v-if="processedContent" :class="{ 'max-w-[750px]': compact }">
            <div class="relative overflow-hidden transition-[max-height] duration-500 ease-in-out" :style="clampStyle">
                <div ref="contentEl" class="content-block font-Mona text-sm" v-html="processedContent" />
                <div
                    v-if="compact && needsToggle"
                    :class="collapsed ? 'opacity-100' : 'opacity-0'"
                    class="pointer-events-none absolute bottom-0 left-0 h-10 w-full bg-gradient-to-t from-white to-transparent transition-opacity duration-500"></div>
            </div>
            <button
                v-if="compact && needsToggle"
                @click="toggle"
                class="mt-2 flex items-center gap-1 text-sm font-bold">
                <template v-if="expanded">{{ $snippets['headless.plp.readLess'] || 'Lees minder' }}</template>
                <template v-else>{{ $snippets['headless.plp.readMore'] || 'Lees meer' }}</template>
                <ChevronDownIcon :class="{ 'rotate-180': expanded }" class="size-4 transition-transform duration-300" />
            </button>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { computed } from 'vue';
    import { ChevronDownIcon } from '@heroicons/vue/24/solid';
    import { extractFaqFromHtml } from '~/utils/seo';

    const props = defineProps<{ title?: string; content?: string; compact?: boolean }>();
    const processedContent = computed(() => {
        return props.content ? (useRemoveStyle(props.content) ?? '') : '';
    });

    // Brand and category SEO texts carry their FAQ as "<h3>question?</h3><p>answer</p>". The full
    // text is always in the DOM (compact mode only clips it visually), so the questions this picks
    // up are the questions the visitor can read.
    useFaqPageSchema(() => extractFaqFromHtml(processedContent.value));

    // Collapsed height of the compact (read more) mode
    const COLLAPSED_HEIGHT = 84;

    const contentEl = ref<HTMLElement>();
    const expanded = ref(false);
    // Full content height, measured on the client. max-height needs a pixel value on
    // both ends to animate — transitioning to `none` snaps instead of easing.
    const contentHeight = ref(0);
    // The full text is always in the SSR HTML (only visually clipped), so search
    // engines index everything. Assume the toggle is needed until measured on the
    // client, then hide it when the content fits within the collapsed height.
    const needsToggle = ref(!!props.compact);
    const collapsed = computed(() => props.compact && needsToggle.value && !expanded.value);

    const clampStyle = computed(() => {
        if (!props.compact || !needsToggle.value) return undefined;
        return { maxHeight: expanded.value ? `${contentHeight.value}px` : `${COLLAPSED_HEIGHT}px` };
    });

    const measure = () => {
        if (!props.compact || !contentEl.value) return;
        contentHeight.value = contentEl.value.scrollHeight;
        needsToggle.value = contentHeight.value > COLLAPSED_HEIGHT;
    };

    const toggle = () => {
        // Re-measure at click time so the expand target reflects the current viewport width
        measure();
        expanded.value = !expanded.value;
    };

    onMounted(() => {
        nextTick(measure);
        window.addEventListener('resize', measure);
    });
    onUnmounted(() => window.removeEventListener('resize', measure));
    watch(processedContent, () => nextTick(measure));
</script>
